import CreateGroup from "../../pages/stage/CreateGroup";
import ListTeams from "../../pages/stage/ListTeams";
import React, { useMemo } from "react";
import { toast } from "react-toastify";
import AddTeamToGroupMutation from "./mutation/AddTeamToGroup";
import RemoveTeamFromGroupMutation from "./mutation/RemoveTeamFromGroup";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function GroupsList({
                                       activeSeasonData,
                                       stageId,
                                       scheduleType,
                                       teams,
                                       setTeams,
                                       groups,
                                       setGroups,
                                   }) {
    const { t } = useTranslation();
    const colors = useThemeColors();

    const [addTeamToGroup] = AddTeamToGroupMutation();
    const [removeTeamFromGroup] = RemoveTeamFromGroupMutation();

    const ui = useMemo(() => {
        return {
            wrap: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            headerRight: {
                display: "flex",
                alignItems: "center",
                gap: 10,
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
                whiteSpace: "nowrap",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            body: { padding: 14 },
            sectionCard: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
            },
            gridTop: {
                display: "grid",
                gridTemplateColumns: "1fr 2fr",
                gap: 12,
                alignItems: "start",
            },
            actionCard: {
                borderRadius: 16,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "rgba(0,0,0,0.012)",
            },
            actionTitle: {
                margin: 0,
                fontWeight: 900,
                fontSize: 12.5,
                color: colors.textOnPrimary,
                textTransform: "uppercase",
                letterSpacing: "0.02em",
            },
            actionSub: {
                margin: "6px 0 0 0",
                fontWeight: 700,
                color: colors.secondary,
                fontSize: 12.5,
            },
            buttonRow: {
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                marginTop: 10,
            },
            infoPill: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                color: colors.textOnPrimary,
                fontWeight: 900,
                fontSize: 12,
                whiteSpace: "nowrap",
            },
            muted: {
                color: "rgba(20,21,26,0.60)",
                fontWeight: 700,
                fontSize: 12.5,
            },
        };
    }, [colors.primary, colors.secondary, colors.textOnPrimary]);

    const shuffle = (array) => {
        const copy = [...array];
        for (let i = copy.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            const tmp = copy[i];
            copy[i] = copy[j];
            copy[j] = tmp;
        }
        return copy;
    };

    const shuffleTeamsInGroups = () => {
        const teamsToShuffle = teams.filter((team) => team.status === "todo");
        const teamsShuffled = shuffle(teamsToShuffle);
        const groupsLength = groups.length;

        if (groupsLength === 0) {
            toast.error(t("groupsList.alerts.createGroupsFirst"), {
                autoClose: 3000,
                toastId: "random_error",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }

        let groupIndex = -1;
        const newTeams = teamsShuffled.map((team) => {
            groupIndex += 1;
            if (groupIndex >= groupsLength) groupIndex = 0;
            return { ...team, status: groups[groupIndex].id };
        });

        addTeamToGroup({
            variables: {
                stageId: parseInt(stageId, 10),
                seasonId: parseInt(activeSeasonData.id, 10),
                groups: newTeams.map((team) => ({
                    teamId: parseInt(team.id, 10),
                    groupId: team.status,
                    teamAliasName: team.id,
                })),
            },
        }).catch((err) => {
            toast.error(getLocalizedGraphQLError(err, t, "groupsList.alerts.addTeamGroupError"), {
                autoClose: 3000,
                toastId: "add_team_group_error",
                position: "top-center",
                closeOnClick: true,
            });
        });

        const byId = new Map(newTeams.map((team) => [String(team.id), team]));
        const merged = teams.map((team) => byId.get(String(team.id)) || team);
        setTeams(merged);
    };

    const cleanGroups = () => {
        removeTeamFromGroup({
            variables: {
                stageId: parseInt(stageId, 10),
                seasonId: parseInt(activeSeasonData.id, 10),
            },
        }).catch((err) => {
            toast.error(getLocalizedGraphQLError(err, t, "groupsList.alerts.cleanGroupsError"), {
                autoClose: 3000,
                toastId: "clean_groups_error",
                position: "top-center",
                closeOnClick: true,
            });
        });

        const newTeams = teams.map((team) => ({ ...team, status: "todo" }));
        setTeams(newTeams);
    };

    const totalTeams = teams?.length || 0;
    const totalGroups = groups?.length || 0;
    const seasonName =
        activeSeasonData?.name || t("groupsList.header.fallbackSeason");

    return (
        <div id="kbola-groups-list" style={ui.wrap}>
            <div style={ui.header}>
                <div style={ui.titleWrap}>
                    <span style={ui.badge}>{totalGroups}</span>

                    <div style={{ minWidth: 0 }}>
                        <h3 style={ui.title}>{t("groupsList.header.title")}</h3>
                        <p style={ui.sub}>
                            {t("groupsList.header.subtitle", { seasonName })}
                        </p>
                    </div>
                </div>

                <div style={ui.headerRight}>
                    <span
                        style={ui.infoPill}
                        title={t("groupsList.info.teamsInStage")}
                    >
                        <i className="fas fa-users" />
                        {t("groupsList.info.teamsCount", { count: totalTeams })}
                    </span>

                    <span
                        style={ui.infoPill}
                        title={t("groupsList.info.scheduleType")}
                    >
                        <i className="fas fa-layer-group" />
                        {scheduleType || t("groupsList.info.emptyValue")}
                    </span>
                </div>
            </div>

            <div style={ui.body}>
                <div style={ui.sectionCard}>
                    <div style={ui.gridTop} className="kbola-groups-gridTop">
                        <div style={ui.actionCard}>
                            <div>
                                <p style={ui.actionTitle}>
                                    {t("groupsList.sections.draftTools")}
                                </p>
                                <p style={ui.actionSub}>
                                    {t("groupsList.sections.draftToolsDescription")}
                                </p>
                            </div>

                            <div style={ui.buttonRow}>
                                <button
                                    type="button"
                                    style={{ ...ui.btn, ...ui.btnPrimary }}
                                    onClick={shuffleTeamsInGroups}
                                    title={t("groupsList.tooltips.randomDraft")}
                                >
                                    <i className="fas fa-random" />
                                    {t("groupsList.buttons.randomDraft")}
                                </button>

                                <button
                                    type="button"
                                    style={ui.btn}
                                    onClick={cleanGroups}
                                    title={t("groupsList.tooltips.restartDraft")}
                                >
                                    <i className="fas fa-undo" />
                                    {t("groupsList.buttons.restartDraft")}
                                </button>
                            </div>

                            {totalGroups === 0 ? (
                                <div style={{ marginTop: 12 }}>
                                    <div
                                        style={{
                                            ...ui.infoPill,
                                            background: "rgba(220,53,69,0.08)",
                                            color: "#dc3545",
                                            borderColor: "rgba(220,53,69,0.22)",
                                        }}
                                    >
                                        <i className="fas fa-exclamation-triangle" />
                                        {t("groupsList.emptyState.createGroupsFirst")}
                                    </div>

                                    <div style={{ ...ui.muted, marginTop: 8 }}>
                                        {t("groupsList.emptyState.createGroupsFirstDescription")}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div style={ui.actionCard}>
                            <p style={ui.actionTitle}>
                                {t("groupsList.sections.groups")}
                            </p>
                            <p style={ui.actionSub}>
                                {t("groupsList.sections.groupsDescription")}
                            </p>

                            <div style={{ marginTop: 10 }}>
                                <CreateGroup
                                    groups={groups}
                                    setGroups={setGroups}
                                    stageId={stageId}
                                    scheduleType={scheduleType}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                        <ListTeams
                            teams={teams}
                            setTeams={setTeams}
                            groups={groups}
                            setGroups={setGroups}
                            stageId={stageId}
                            seasonId={activeSeasonData.id}
                        />
                    </div>
                </div>
            </div>

            <style>{`
                #kbola-groups-list button:focus,
                #kbola-groups-list a:focus {
                    outline: none;
                    box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }

                @media (max-width: 980px) {
                    #kbola-groups-list .kbola-groups-gridTop {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </div>
    );
}
